import {Component, Input, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {
  AppAction,
  multilevelTabChangedTo,
  npvLocationDepartmentColumnsChangedTo,
  npvLocationProviderColumnsChangedTo,
  npvLocationSpecialtyColumnsChangedTo,
  npvLocationViewByNodeChangedTo
} from '../../../store/actions';
import {NgRedux, select} from '@angular-redux/store';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {
  BaseColumn,
  IAppState,
  INITIAL_STATE,
  NewPatientVisitsLocationVariables,
  SummaryData
} from '../../../store/IAppState';
import {getSingularOntologyLevelName, Page, pluralizeText} from '../../../shared/helpers';
import {BenchmarkOption, ColumnType, MultilevelTab} from '../../../shared/enums';
import {TabNavigationElement} from '../../../tab-navigation/tab-helper';
import * as _ from 'lodash';
import {LOCATION_DEPARTMENT_TAB, LOCATION_PROVIDER_TAB, LOCATION_SPECIALTY_TAB} from '../../../shared/constants';
import {
  MergedNewPatientVisitSnapshotEntry,
  MergedNpvLocationAggregatedByNode,
  MergedProviderNewPatientVisitMultiLevelData,
  NpvByLocationForChart,
  NpvLocationAggregationAndZeroSuppressionCount,
  npvLocationDepartmentColumns,
  npvLocationProviderColumns,
  npvLocationSpecialtyColumns,
  NpvLocationWithSnapshotEntries
} from '../npv-models';
import {DataTableColumns} from '../../../shared/data-table-columns';
import {BenchmarkType, readableNameOfColumnDef} from '../../../shared/benchmark-types';
import {DateRange, NewPatientVisitSummary} from '../../../shared/models';
import {aDefaultNewPatientVisitsLocationVariables} from '../npv-models.spec';
import {
  convertNpvBenchmarkDependentColumnToSelectedBenchmark,
  getDesignatedNpvBenchmarkObject,
  getLevelSpecificNpvLocationTableData,
  sortSnapshotEntries
} from '../npv-helpers';
import {getValueFromObjectWithStringIndexing} from '../../../shared/object-helpers';
import {distinctUntilChanged} from 'rxjs/operators';
import {ColumnWithMatSort} from './location-npv-table/npv-location-table-header/npv-location-table-header.component';

@Component({
  selector: 'app-page-new-patient-visits-location',
  templateUrl: './page-new-patient-visits-location.component.html',
  styleUrls: ['./page-new-patient-visits-location.component.scss']
})
export class PageNewPatientVisitsLocationComponent implements OnChanges, OnInit, OnDestroy {

  levelTabs: TabNavigationElement[] = [
    _.cloneDeep(LOCATION_DEPARTMENT_TAB),
    _.cloneDeep(LOCATION_SPECIALTY_TAB),
    _.cloneDeep(LOCATION_PROVIDER_TAB),
  ];
  readonly multilevelTabChangedTo = multilevelTabChangedTo;
  reducerAction: (columns: BaseColumn[]) => AppAction;
  variables: NewPatientVisitsLocationVariables = aDefaultNewPatientVisitsLocationVariables;
  ontologyText = '';
  originalColumns: DataTableColumns[] = [];
  displayedColumns: DataTableColumns[] = [];
  levelSpecificChartData: NpvByLocationForChart[] = [];
  inverseChartData: NpvByLocationForChart[] = [];
  levelSpecificTableData: NpvLocationWithSnapshotEntries[] = [];
  mergedNpvByLocationByMultilevelData: MergedNpvLocationAggregatedByNode[];
  countOfSuppressedEntries: number;
  snapshotEntryName: string;
  showPatientVisitCountsOnGraph = false;
  dateRange: DateRange;
  summaryData: SummaryData<NewPatientVisitSummary>;
  tableTitle = '';
  showLocationLoader = true;
  showSnapshotLoader = true;
  referToSnapshotLoader = false;
  currentPage: Page;

  @select(['display', 'newPatientVisitsLocationVariables'])
  private readonly newPatientVisitsLocationVariables$: Observable<NewPatientVisitsLocationVariables>;
  @select(['data', 'summaryNewPatientVisitData'])
  private readonly summaryNewPatientVisitData$: Observable<SummaryData<NewPatientVisitSummary>>;
  @select(['display', 'patientVisitCounts'])
  private readonly patientVisitCounts$: Observable<boolean>;
  @select(['data', 'mergedNpvLocationAggregatedByNode'])
  private readonly mergedNpvLocationAggregatedByNode$: Observable<MergedNpvLocationAggregatedByNode[]>;
  @select(['data', 'newPatientVisitMultilevelData'])
  private readonly newPatientVisitMultilevelData$: Observable<MergedProviderNewPatientVisitMultiLevelData>;
  @Input() selectedLevelTab: MultilevelTab = MultilevelTab.LOCATION_DEPARTMENT;
  @Input() benchmarkType: BenchmarkType = BenchmarkType.Mean;
  @Input() benchmarkOption: BenchmarkOption = BenchmarkOption.Academic;
  @Input() isZeroSuppressionChecked = false;

  private subscription: Subscription;
  private newPatientVisitMultilevelData: MergedProviderNewPatientVisitMultiLevelData;

  constructor(private ngRedux: NgRedux<IAppState>) { }

  ngOnInit(): void {
    this.subscription = combineLatest([this.newPatientVisitsLocationVariables$.pipe(distinctUntilChanged(
      (a, b) => _.isEqual(a, b))),
      this.mergedNpvLocationAggregatedByNode$.pipe(distinctUntilChanged(
        (a, b) => _.isEqual(a, b))),
      this.newPatientVisitMultilevelData$,
      this.summaryNewPatientVisitData$, this.patientVisitCounts$]).subscribe(([variables,
       mergedNpvByLocationByMultilevelData, newPatientVisitMultilevelData, summaryNewPatientVisitData,
       showPatientVisitCountsOnGraph]: [NewPatientVisitsLocationVariables, MergedNpvLocationAggregatedByNode[],
      MergedProviderNewPatientVisitMultiLevelData, SummaryData<NewPatientVisitSummary>, boolean]) => {
      this.variables = variables;
      this.mergedNpvByLocationByMultilevelData = mergedNpvByLocationByMultilevelData;
      this.newPatientVisitMultilevelData = newPatientVisitMultilevelData;
      this.summaryData = summaryNewPatientVisitData;
      this.showPatientVisitCountsOnGraph = showPatientVisitCountsOnGraph;
      this.showLocationLoader = _.isEqual(INITIAL_STATE.data.mergedNpvLocationAggregatedByNode,
        mergedNpvByLocationByMultilevelData);
      this.showSnapshotLoader = _.isEqual(INITIAL_STATE.data.newPatientVisitMultilevelData,
        newPatientVisitMultilevelData);
      this.referToSnapshotLoader = !variables.currentSelectedLocationNode && variables.viewByNode;
      this.ensureDataSourcesAreProperlySorted(variables);
      this.ngOnChanges();
    });
  }

  ngOnChanges(): void {
    this.ontologyText = getSingularOntologyLevelName(this.selectedLevelTab);
    this.setChildComponentInputValues(this.mergedNpvByLocationByMultilevelData, this.newPatientVisitMultilevelData,
      this.selectedLevelTab);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  ensureDataSourcesAreProperlySorted(variables: NewPatientVisitsLocationVariables): void {
    const sortDirection = variables.columnForSortingTheData.sort || '';
    const sortAscending = sortDirection === 'asc';
    if (variables.columnForSortingTheData.column.columnType === ColumnType.LOCATION) {
      this.mergedNpvByLocationByMultilevelData = this.mergedNpvByLocationByMultilevelData.sort((a, b) => {
        return sortAscending ? a.memberLocationName.localeCompare(b.memberLocationName) :
          b.memberLocationName.localeCompare(a.memberLocationName);
      });
      return;
    }
    this.sortTheDataSources(variables.columnForSortingTheData, sortAscending);
  }

  private sortTheDataSources(columnWithMatSort: ColumnWithMatSort, sortAscending: boolean): void {
    this.mergedNpvByLocationByMultilevelData.forEach(x => {
      const ontologyName = `${getSingularOntologyLevelName(this.selectedLevelTab).toLowerCase()}NodeName`;
      const tieBreaker = (item: MergedNewPatientVisitSnapshotEntry) => {
        return getValueFromObjectWithStringIndexing(item, ontologyName);
      };
      x.departmentNpvSnapshotData = sortSnapshotEntries(x.departmentNpvSnapshotData, columnWithMatSort.column, tieBreaker,
        sortAscending);
      x.specialtyNpvSnapshotData = sortSnapshotEntries(x.specialtyNpvSnapshotData, columnWithMatSort.column, tieBreaker,
        sortAscending);
      x.providerNpvSnapshotData = sortSnapshotEntries(x.providerNpvSnapshotData, columnWithMatSort.column, tieBreaker,
        sortAscending);
    });
  }

  private setChildComponentInputValues(mergedNpvByLocationByMultilevelData: MergedNpvLocationAggregatedByNode[],
                                       newPatientVisitMultilevelData: MergedProviderNewPatientVisitMultiLevelData,
                                       tab: MultilevelTab): void {
    switch (tab) {
      case MultilevelTab.LOCATION_DEPARTMENT:
        this.setChildComponentLevelSpecificInputValues(mergedNpvByLocationByMultilevelData,
          newPatientVisitMultilevelData?.departmentNpvSnapshotData || [], tab);
        this.originalColumns = npvLocationDepartmentColumns;
        this.updateBenchmarkDependentColumns();
        this.displayedColumns = this.updateColumnFilter(this.variables.displayedDepartmentColumns, this.originalColumns);
        this.reducerAction = npvLocationDepartmentColumnsChangedTo;
        this.currentPage = Page.NPVLocationDepartment;
        break;
      case MultilevelTab.LOCATION_SPECIALTY:
        this.setChildComponentLevelSpecificInputValues(mergedNpvByLocationByMultilevelData,
          newPatientVisitMultilevelData?.specialtyNpvSnapshotData || [], tab);
        this.originalColumns = npvLocationSpecialtyColumns;
        this.updateBenchmarkDependentColumns();
        this.displayedColumns = this.updateColumnFilter(this.variables.displayedSpecialtyColumns, this.originalColumns);
        this.reducerAction = npvLocationSpecialtyColumnsChangedTo;
        this.currentPage = Page.NPVLocationSpecialty;
        break;
      case MultilevelTab.LOCATION_PROVIDER:
        this.setChildComponentLevelSpecificInputValues(mergedNpvByLocationByMultilevelData,
          newPatientVisitMultilevelData?.providerNpvSnapshotData || [], tab);
        this.originalColumns = npvLocationProviderColumns;
        this.updateBenchmarkDependentColumns();
        this.displayedColumns = this.updateColumnFilter(this.variables.displayedProviderColumns, this.originalColumns);
        this.reducerAction = npvLocationProviderColumnsChangedTo;
        this.currentPage = Page.NPVLocationProvider;
    }
  }

  private setChildComponentLevelSpecificInputValues(
    mergedNpvByLocationByMultilevelData: MergedNpvLocationAggregatedByNode[], snapshotEntries:
      MergedNewPatientVisitSnapshotEntry[], tab: MultilevelTab) {
    const tabName: string = getSingularOntologyLevelName(tab);
    this.tableTitle = `NPV by Location by ${tabName}`;
    this.snapshotEntryName = pluralizeText(tabName);
    let locationChartData: NpvByLocationForChart[];
    if (this.variables.currentSelectedOntologyNode) {
      locationChartData = [];
      mergedNpvByLocationByMultilevelData.forEach(entry => {
        const designatedNodeList: MergedNewPatientVisitSnapshotEntry[] = getValueFromObjectWithStringIndexing(
          entry, `${tabName.toLowerCase()}NpvSnapshotData`);
        designatedNodeList.filter(node => node[`${tabName.toLowerCase()}NodePath`].localeCompare(
          this.variables.currentSelectedOntologyNode?.identity || '') === 0).forEach(matchingNode => {
          locationChartData.push({
            countOfNewPatientVisits: matchingNode.countOfNewPatientVisits,
            countOfTotalPatientVisits: matchingNode.countOfTotalPatientVisits,
            newPatientVisitsPercentage: matchingNode.newPatientVisitsPercentage,
            countOfExistingPatientVisits: matchingNode.countOfExistingPatientVisits,
            name: entry.memberLocationName, identity: `${entry.memberLocationKey}`,
            benchmarkValue: -1
          });
        });
      });
    } else {
      locationChartData = mergedNpvByLocationByMultilevelData?.map(entry => {
        return {
          countOfNewPatientVisits: entry.countOfNewPatientVisits,
          countOfTotalPatientVisits: entry.countOfTotalPatientVisits,
          newPatientVisitsPercentage: entry.newPatientVisitsPercentage,
          countOfExistingPatientVisits: entry.countOfExistingPatientVisits,
          name: entry.memberLocationName, identity: `${entry.memberLocationKey}`,
          benchmarkValue: -1
        };
      }) || [];
    }
    let snapshotChartData: NpvByLocationForChart[];
    if (this.variables.currentSelectedLocationNode) {
      snapshotChartData = [];
      const matchingEntry = mergedNpvByLocationByMultilevelData.find(entry => entry.memberLocationKey ===
        +(this.variables.currentSelectedLocationNode?.identity || 0));
      const designatedSnapshotDimension: MergedNpvLocationAggregatedByNode[] = matchingEntry ?
        getValueFromObjectWithStringIndexing(matchingEntry, `${tabName.toLowerCase()}NpvSnapshotData`) : [];
      designatedSnapshotDimension.forEach(dimension => {
        if (!this.isZeroSuppressionChecked || dimension.countOfTotalPatientVisits > 0) {
          snapshotChartData.push({
            countOfNewPatientVisits: dimension.countOfNewPatientVisits,
            countOfTotalPatientVisits: dimension.countOfTotalPatientVisits,
            newPatientVisitsPercentage: dimension.newPatientVisitsPercentage,
            countOfExistingPatientVisits: dimension.countOfExistingPatientVisits,
            name: getValueFromObjectWithStringIndexing(dimension, `${tabName.toLowerCase()}NodeName`),
            identity: getValueFromObjectWithStringIndexing(dimension, `${tabName.toLowerCase()}NodePath`),
            benchmarkValue: 100 * getValueFromObjectWithStringIndexing(dimension, getDesignatedNpvBenchmarkObject(
              this.benchmarkOption), `benchmark${readableNameOfColumnDef(this.benchmarkType)}`)
          });
        }
      });
    } else {
      snapshotChartData = snapshotEntries?.filter(x => !this.isZeroSuppressionChecked ||
        x.countOfTotalPatientVisits > 0).map(entry => {
        return {
          countOfNewPatientVisits: entry.countOfNewPatientVisits,
          countOfTotalPatientVisits: entry.countOfTotalPatientVisits,
          newPatientVisitsPercentage: entry.newPatientVisitsPercentage,
          countOfExistingPatientVisits: entry.countOfExistingPatientVisits,
          name: entry[`${tabName.toLowerCase()}NodeName`], identity: `${entry[`${tabName.toLowerCase()}NodePath`]}`,
          benchmarkValue: 100 * entry[getDesignatedNpvBenchmarkObject(this.benchmarkOption)]
            [`benchmark${readableNameOfColumnDef(this.benchmarkType)}`]
        };
      });
    }
    locationChartData.sort((a, b) => {
      return b.newPatientVisitsPercentage - a.newPatientVisitsPercentage;
    });
    if (this.variables.viewByNode) {
      this.levelSpecificChartData = snapshotChartData;
      this.inverseChartData = locationChartData;
    } else {
      this.levelSpecificChartData = locationChartData;
      this.inverseChartData = snapshotChartData;
    }
    const aggregationAndZeroSuppressionCount: NpvLocationAggregationAndZeroSuppressionCount =
      getLevelSpecificNpvLocationTableData(true, tab, this.isZeroSuppressionChecked,
        mergedNpvByLocationByMultilevelData);
    this.levelSpecificTableData = aggregationAndZeroSuppressionCount.entries;
    this.countOfSuppressedEntries = aggregationAndZeroSuppressionCount.countOfZeroSuppressedEntries;
  }

  private updateBenchmarkDependentColumns() {
    for (let x = 0; x < this.originalColumns.length; x++) {
      switch (this.originalColumns[x].columnType) {
        case ColumnType.BENCHMARK:
          convertNpvBenchmarkDependentColumnToSelectedBenchmark(
            this.originalColumns[x], this.benchmarkType, this.benchmarkOption, false);
          break;
        case ColumnType.VARIANCE:
          convertNpvBenchmarkDependentColumnToSelectedBenchmark(
            this.originalColumns[x], this.benchmarkType, this.benchmarkOption, true);
      }
    }
  }

  toggleViewByNode(viewBy: boolean): void {
    this.ngRedux.dispatch(npvLocationViewByNodeChangedTo(viewBy));
  }

  // TODO: Can this be a shared function?
  private updateColumnFilter(displayedColumns: BaseColumn[], columns: DataTableColumns[]): DataTableColumns[] {
    const displayedColumnDefs: string[] = displayedColumns.map(col => {
      switch (col.columnType) {
        case ColumnType.BENCHMARK:
          return `benchmark${readableNameOfColumnDef(this.benchmarkType)}`;
        case ColumnType.VARIANCE:
          return `variance${readableNameOfColumnDef(this.benchmarkType)}`;
        default:
          return col.columnDef;
      }
    });
    return columns.filter(col => col.primaryColumn || displayedColumnDefs.includes(col.columnDef)).slice();
  }
}
